/**
 * Preset Demo Scenarios
 * Pre-configured prompts showcasing different routing decisions
 */

import type { DemoScenario } from '@/lib/types';

export const DEMO_SCENARIOS: Record<string, DemoScenario> = {
  'simple-qa': {
    id: 'simple-qa',
    name: 'Simple Q&A',
    prompt: 'What is the capital of France?',
    expectedModel: 'gemini-2.5-flash-lite',
    expectedSavings: 95,
    description: 'Simple factual question - routes to fastest, cheapest model',
  },
  'code-generation': {
    id: 'code-generation',
    name: 'Code Generation',
    prompt:
      'Write a Python function to implement a binary search tree with insert, delete, and search operations. Include docstrings and type hints.',
    expectedModel: 'claude-sonnet-4-5',
    expectedSavings: 0,
    description: 'Complex coding task - routes to premium model for quality',
  },
  'creative-writing': {
    id: 'creative-writing',
    name: 'Creative Writing',
    prompt:
      'Write a short story about a time traveler who discovers that changing the past creates parallel universes instead of altering the current timeline.',
    expectedModel: 'gpt-5',
    expectedSavings: 30,
    description: 'Creative task - balances quality with cost',
  },
  'data-analysis': {
    id: 'data-analysis',
    name: 'Data Analysis',
    prompt:
      'Analyze this sales data and identify trends: Q1: $120K, Q2: $135K, Q3: $142K, Q4: $155K. What should we focus on next year?',
    expectedModel: 'gemini-2.5-flash',
    expectedSavings: 60,
    description: 'Analytical task - uses mid-tier model for cost efficiency',
  },
  debugging: {
    id: 'debugging',
    name: 'Code Debugging',
    prompt: `Debug this Python code that's throwing a KeyError:
\`\`\`python
user_data = {"name": "Alice"}
print(user_data["age"])
\`\`\``,
    expectedModel: 'claude-sonnet-4-5',
    expectedSavings: 0,
    description: 'Code debugging - uses best model for accuracy',
  },
  translation: {
    id: 'translation',
    name: 'Translation',
    prompt: 'Translate this text to Spanish: "Hello, how are you today?"',
    expectedModel: 'gemini-2.5-flash',
    expectedSavings: 70,
    description: 'Simple translation - efficient model selection',
  },
  summarization: {
    id: 'summarization',
    name: 'Long Document Summary',
    prompt:
      'Summarize this 5000-word research paper on climate change impacts... [long text]',
    expectedModel: 'claude-haiku-4-5',
    expectedSavings: 85,
    description: 'Summarization - fast model optimized for large context',
  },
  reasoning: {
    id: 'reasoning',
    name: 'Complex Reasoning',
    prompt:
      'If a train leaves Chicago at 60mph heading to New York (800 miles), and another leaves New York at 80mph heading to Chicago 30 minutes later, where do they meet and when?',
    expectedModel: 'claude-sonnet-4-5',
    expectedSavings: 0,
    description: 'Complex reasoning - requires advanced model',
  },
};

export const SCENARIO_OPTIONS = Object.values(DEMO_SCENARIOS);

export function getScenarioById(id: string): DemoScenario | undefined {
  return DEMO_SCENARIOS[id];
}
